import { useSpaceGroupsContext } from "@circle-react/contexts/spaceGroupsContext";
import { useNavigate } from "@tanstack/react-router";
import { useMutation } from "react-query";

import { t } from "@/i18n-js/instance";
import { spaceGroupApi } from "@/react/api/spaceGroupApi";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import type { ApiError } from "@/react/config/CustomErrors";
import type { SpaceGroup } from "@/react/types/SpaceGroup";

interface SpaceGroupFormData {
  add_members_to_space_group_on_space_join?: boolean;
  allow_members_to_create_spaces?: boolean;
  automatically_add_members_to_new_spaces?: boolean;
  hide_members_count?: boolean;
  hide_non_member_spaces_from_sidebar?: boolean;
  is_hidden_from_non_members?: boolean;
  name?: string;
  slug?: string;
}

interface UseCreateSpaceGroupMutationProps {
  onSuccess: () => void;
  position?: number;
}

export const useCreateSpaceGroupMutation = ({
  position,
  onSuccess,
}: UseCreateSpaceGroupMutationProps) => {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { actions } = useSpaceGroupsContext();

  const mutationFn: (
    formData: SpaceGroupFormData,
  ) => Promise<SpaceGroup> = formData =>
    spaceGroupApi.create({
      space_group: { ...formData, position },
    });

  return useMutation<SpaceGroup, ApiError, SpaceGroupFormData>({
    mutationFn,
    onSuccess: response => {
      success(t("space_groups.form.create.saved_successfully"));
      actions.refresh();
      onSuccess();
      void navigate({
        to: "/s/$spaceGroupSlug",
        params: { spaceGroupSlug: response.slug },
      });
    },
    onError: mutationError => {
      const errorMessage =
        typeof mutationError.errorDetails === "string"
          ? mutationError.errorDetails
          : t("space_groups.form.create.save_error");

      error(errorMessage);
    },
  });
};
